import React, { useState, useMemo, useEffect } from 'react';
import { fmtNum, fmtFull } from '../utils/formatters';
import { Sparkline } from './KPI';
import { Campaign } from '../data';
import { getCampaigns, getReachCampaigns, getCampaignsImpressionsSummary } from '../adapters/campaigns';

const THUMBS: Record<string, { from: string; to: string; label: string; sub: string; icon: string }> = {
  broadway:   { from: '#dc2626', to: '#7c3aed', label: 'BROADWAY',       sub: 'ST × HP',      icon: '★' },
  hamilton:   { from: '#0f172a', to: '#92400e', label: 'HAMILTON',       sub: '10 YEARS',     icon: '★' },
  wicked:     { from: '#065f46', to: '#22c55e', label: 'WICKED',         sub: 'PART TWO',     icon: '♢' },
  glasshouse: { from: '#f0abfc', to: '#7c3aed', label: 'GLASSHOUSE',     sub: 'SPRING BLOOM', icon: '◐' },
  bloomies:   { from: '#fde047', to: '#000000', label: "BLOOMINGDALE'S", sub: 'LOYALTY',      icon: '◇' },
  pepsi:      { from: '#1e40af', to: '#0ea5e9', label: 'BUBLY',          sub: 'SPARKLE',      icon: '○' },
};

function Thumb({ kind, imageUrl, size = 'md' }: { kind: string; imageUrl?: string; size?: 'md' | 'lg' }) {
  const t = THUMBS[kind] || { from: '#475569', to: '#1e293b', label: 'CAMPAIGN', sub: '', icon: '◯' };
  const H = size === 'lg' ? 160 : 96;

  if (imageUrl) {
    return (
      <div style={{ width: '100%', height: H, borderRadius: 10, overflow: 'hidden', flexShrink: 0 }}>
        <img src={imageUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
      </div>
    );
  }

  return (
    <div style={{
      width: '100%', height: H,
      background: `linear-gradient(150deg, ${t.from} 0%, ${t.to} 100%)`,
      borderRadius: 10, position: 'relative', overflow: 'hidden', flexShrink: 0,
    }}>
      {/* noise texture */}
      <svg width="100%" height="100%" viewBox="0 0 400 200" preserveAspectRatio="xMidYMid slice"
        style={{ position: 'absolute', inset: 0, opacity: 0.07, mixBlendMode: 'screen' as any }}>
        <filter id={`noise-${kind}`}>
          <feTurbulence type="fractalNoise" baseFrequency="0.75" numOctaves="4" stitchTiles="stitch" />
          <feColorMatrix type="saturate" values="0" />
        </filter>
        <rect width="400" height="200" filter={`url(#noise-${kind})`} />
      </svg>
      {/* radial highlight */}
      <div style={{
        position: 'absolute', inset: 0,
        background: 'radial-gradient(ellipse 70% 60% at 30% 40%, rgba(255,255,255,0.18) 0%, transparent 70%)',
      }} />
      {/* bottom scrim */}
      <div style={{
        position: 'absolute', bottom: 0, left: 0, right: 0, height: '55%',
        background: 'linear-gradient(to top, rgba(0,0,0,0.45) 0%, transparent 100%)',
      }} />
      {/* large watermark icon */}
      <div style={{
        position: 'absolute', right: size === 'lg' ? -10 : -6, top: '50%', transform: 'translateY(-52%)',
        fontSize: size === 'lg' ? 100 : 64, lineHeight: 1, color: 'rgba(255,255,255,0.12)',
        fontWeight: 900, letterSpacing: '-0.06em', userSelect: 'none',
      }}>{t.icon}</div>
      <div style={{ position: 'absolute', bottom: size === 'lg' ? 14 : 9, left: size === 'lg' ? 14 : 10, right: 10, color: '#fff' }}>
        <div style={{ fontSize: size === 'lg' ? 14 : 11, fontWeight: 800, letterSpacing: '0.12em', textTransform: 'uppercase', textShadow: '0 1px 6px rgba(0,0,0,0.5)' }}>{t.label}</div>
        {t.sub && <div style={{ fontSize: size === 'lg' ? 11 : 9, fontWeight: 600, letterSpacing: '0.16em', textTransform: 'uppercase', opacity: 0.72, marginTop: 1 }}>{t.sub}</div>}
      </div>
    </div>
  );
}

const STATUS_STYLE: Record<string, { bg: string; border: string; fg: string; dot: string }> = {
  Active:    { bg: 'var(--accent-soft-bg)', border: 'var(--accent-soft-border)', fg: 'var(--brand-green-500)', dot: 'var(--brand-green-500)' },
  Scheduled: { bg: 'rgba(245,158,11,0.10)', border: 'rgba(245,158,11,0.30)',     fg: '#f59e0b',                dot: '#f59e0b' },
  Ended:     { bg: 'var(--bg-surface-3)',   border: 'var(--border-soft)',         fg: 'var(--fg-muted)',        dot: 'var(--fg-faint)' },
};

function StatusPill({ status }: { status: string }) {
  const s = STATUS_STYLE[status] || STATUS_STYLE.Ended;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 10.5, fontWeight: 700,
      background: s.bg, color: s.fg, border: '1px solid ' + s.border, padding: '2px 9px', borderRadius: 999,
    }}>
      <span style={{ width: 6, height: 6, borderRadius: '50%', background: s.dot, display: 'inline-block',
        boxShadow: status === 'Active' ? `0 0 0 3px ${s.dot}33` : 'none' }} /> {status}
    </span>
  );
}

function Stat({ label, value, big }: { label: string; value: string; big?: boolean }) {
  return (
    <div style={{ flex: 1, minWidth: 0 }}>
      <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--fg-muted)', marginBottom: 3, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{label}</div>
      <div className="num" style={{ fontSize: big ? 17 : 14, fontWeight: 700, color: 'var(--fg-strong)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{value}</div>
    </div>
  );
}

function PacingBar({ pacing, status }: { pacing: number; status?: string }) {
  const pct = Math.min(100, pacing);
  const color = status === 'Ended'
    ? 'var(--fg-muted)'
    : pacing >= 95 ? 'var(--brand-green-500)' : pacing >= 80 ? '#f59e0b' : '#dc2626';
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <div style={{ flex: 1, height: 6, background: 'var(--bg-surface-3)', borderRadius: 999, overflow: 'hidden' }}>
        <div style={{ width: `${pct}%`, height: '100%', background: color, borderRadius: 999, transition: 'width 0.3s' }} />
      </div>
      <span className="num" style={{ fontSize: 11, fontWeight: 700, color, minWidth: 42, textAlign: 'right' }}>{pacing}%</span>
    </div>
  );
}

function FeaturedCard({ campaign, onOpen }: { campaign: Campaign; onOpen: () => void }) {
  return (
    <div className="card" style={{ padding: 0, overflow: 'hidden', marginBottom: 20, display: 'grid', gridTemplateColumns: '260px 1fr', borderColor: 'var(--accent-soft-border)' }}>
      <div style={{ padding: 16 }}>
        <Thumb kind={campaign.thumb} imageUrl={campaign.imageUrl} size="lg" />
      </div>
      <div style={{ padding: '18px 20px 18px 0', display: 'flex', flexDirection: 'column', gap: 14 }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
          <div style={{ minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
              <span className="eyebrow" style={{ color: 'var(--brand-green-500)' }}>★ Featured · Live</span>
              <StatusPill status={campaign.status} />
            </div>
            <div style={{ fontSize: 22, fontWeight: 700, color: 'var(--fg-strong)', letterSpacing: '-0.01em', marginBottom: 4 }}>{campaign.name}</div>
            <div style={{ fontSize: 12, color: 'var(--fg-muted)' }}>{campaign.client.name} · <span className="num">{campaign.flight}</span> · {campaign.area}</div>
          </div>
          <button onClick={onOpen} style={{
            background: 'var(--brand-navy-800)', color: '#fff', border: 'none', borderRadius: 8,
            padding: '9px 18px', fontSize: 12, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap',
            display: 'inline-flex', alignItems: 'center', gap: 6,
          }}>
            View POP report
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M5 12h14M13 5l7 7-7 7" />
            </svg>
          </button>
        </div>
        <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
          <Stat label="Plays" value={fmtFull(campaign.plays)} big />
          <Stat label="Impressions" value={fmtFull(campaign.impressions)} big />
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 200px', gap: 18, alignItems: 'center' }}>
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4, fontSize: 11 }}>
              <span style={{ color: 'var(--fg-muted)', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase' }}>Pacing</span>
            </div>
            <PacingBar pacing={campaign.pacing} status={campaign.status} />
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
            <div className="eyebrow" style={{ fontSize: 9 }}>vs. prev period</div>
            <div className="num" style={{ fontSize: 18, fontWeight: 700, color: campaign.delta >= 0 ? 'var(--brand-green-500)' : '#dc2626' }}>
              {campaign.delta >= 0 ? '▲' : '▼'} {Math.abs(campaign.delta).toFixed(1)}%
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function CampaignCard({ campaign, onOpen }: { campaign: Campaign; onOpen: () => void }) {
  return (
    <div className="card" style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 14, cursor: 'pointer', transition: 'box-shadow 0.15s, transform 0.15s' }}
      onClick={onOpen}
      onMouseEnter={e => { e.currentTarget.style.boxShadow = 'var(--shadow-card-hi)'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
      onMouseLeave={e => { e.currentTarget.style.boxShadow = 'var(--shadow-card)'; e.currentTarget.style.transform = 'translateY(0)'; }}>
      <Thumb kind={campaign.thumb} imageUrl={campaign.imageUrl} />
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 10 }}>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--fg-strong)', marginBottom: 2, lineHeight: 1.25 }}>{campaign.name}</div>
          <div style={{ fontSize: 11, color: 'var(--fg-muted)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{campaign.client.name}</div>
        </div>
        <StatusPill status={campaign.status} />
      </div>
      <div style={{ display: 'flex', gap: 6, fontSize: 11, color: 'var(--fg-muted)', alignItems: 'center', flexWrap: 'wrap' }}>
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="3" y="4" width="18" height="18" rx="2" /><path d="M3 10h18M8 2v4M16 2v4" />
        </svg>
        <span className="num">{campaign.flight}</span>
        <span style={{ opacity: 0.4 }}>·</span>
        <span>{campaign.area}</span>
      </div>
      {campaign.status !== 'Scheduled' ? (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 6, padding: '10px 0', borderTop: '1px solid var(--border-subtle)', borderBottom: '1px solid var(--border-subtle)' }}>
            <Stat label="Plays" value={fmtNum(campaign.plays)} />
            <Stat label="Impr." value={fmtNum(campaign.impressions)} />
          </div>
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4, fontSize: 10.5 }}>
              <span style={{ color: 'var(--fg-muted)', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase' }}>Pacing</span>
            </div>
            <PacingBar pacing={campaign.pacing} status={campaign.status} />
          </div>
        </>
      ) : (
        <div style={{ padding: '14px 0', textAlign: 'center', borderTop: '1px solid var(--border-subtle)', borderBottom: '1px solid var(--border-subtle)', color: 'var(--fg-muted)', fontSize: 12 }}>
          Launches in <strong style={{ color: 'var(--fg-strong)' }} className="num">
            {Math.max(0, Math.round((new Date(campaign.startDate).getTime() - new Date('2026-05-24').getTime()) / 864e5))}
          </strong> days
        </div>
      )}
      <div style={{ height: 28, marginTop: -4 }}>
        {campaign.status !== 'Scheduled' && campaign.sparkline?.length > 1 ? (
          <Sparkline data={campaign.sparkline} color="var(--brand-green-500)" height={28} />
        ) : (
          <div style={{ height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center', borderTop: '1px dashed var(--border-soft)', color: 'var(--fg-faint)', fontSize: 10, fontStyle: 'italic' }}>
            {campaign.status === 'Scheduled' ? 'No delivery yet' : ''}
          </div>
        )}
      </div>
    </div>
  );
}

function SummaryStrip({ campaigns }: { campaigns: Campaign[] }) {
  const active = campaigns.filter(c => c.status === 'Active');
  const scheduled = campaigns.filter(c => c.status === 'Scheduled');
  const totalSpend = campaigns.reduce((a, c) => a + c.spent, 0);
  const totalBudget = campaigns.reduce((a, c) => a + c.budget, 0);
  const totalPlays = campaigns.reduce((a, c) => a + c.plays, 0);
  const totalImpressions = campaigns.reduce((a, c) => a + c.impressions, 0);
  const totalViews = campaigns.reduce((a, c) => a + c.views, 0);

  const items = [
    { label: 'Active', value: active.length, sub: active.length + ' running now', dot: 'var(--brand-green-500)' },
    { label: 'Scheduled', value: scheduled.length, sub: 'Upcoming flights', dot: '#f59e0b' },
    { label: 'Impressions YTD', value: fmtNum(totalImpressions), sub: fmtNum(totalPlays) + ' plays · ' + fmtNum(totalViews) + ' views' },
    { label: 'Plays YTD', value: fmtNum(totalPlays), sub: fmtNum(totalImpressions) + ' impressions' },
  ];

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 14, marginBottom: 20 }}>
      {items.map(it => (
        <div key={it.label} className="card" style={{ padding: '14px 16px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
            {it.dot && <span style={{ width: 6, height: 6, borderRadius: '50%', background: it.dot, display: 'inline-block',
              boxShadow: it.label === 'Active' ? `0 0 0 3px ${it.dot}33` : 'none' }} />}
            <span className="eyebrow" style={{ fontSize: 10 }}>{it.label}</span>
          </div>
          <div className="num" style={{ fontSize: 26, fontWeight: 700, color: 'var(--fg-strong)', lineHeight: 1, marginBottom: 4 }}>{it.value}</div>
          <div style={{ fontSize: 11, color: 'var(--fg-muted)' }}>{it.sub}</div>
        </div>
      ))}
    </div>
  );
}

interface CampaignsPageProps {
  onOpenCampaign: (c: Campaign) => void;
  clientUuid: string;
  onLoad?: (campaigns: Campaign[]) => void;
}

export function CampaignsPage({ onOpenCampaign, clientUuid, onLoad }: CampaignsPageProps) {
  const [data, setData] = useState<Campaign[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState('All');
  const isReach = clientUuid === '__reach__';

  useEffect(() => {
    if (!clientUuid) return;
    setLoading(true);
    setError(null);
    const fetch = clientUuid === '__reach__' ? getReachCampaigns() : getCampaigns(clientUuid);
    fetch
      .then(result => {
        setData(result);
        onLoad?.(result);
        const toFetch = result.filter(c => c.broadsignId);
        if (!toFetch.length) return;
        getCampaignsImpressionsSummary(toFetch.map(c => c.id))
          .then(impressionsMap => {
            setData(prev => prev.map(c => {
              const imp = impressionsMap[c.id];
              return imp ? { ...c, ...imp } : c;
            }));
          })
          .catch(() => {});
      })
      .catch(err => setError(err?.message ?? 'Failed to load campaigns'))
      .finally(() => setLoading(false));
  }, [clientUuid]);

  const filtered = useMemo(() => {
    const STATUS_ORDER: Record<string, number> = { Active: 0, Scheduled: 1, Ended: 2 };
    let arr = filter === 'All' ? data : data.filter(c => c.status === filter);
    return [...arr].sort((a, b) => {
      const sd = (STATUS_ORDER[a.status] ?? 3) - (STATUS_ORDER[b.status] ?? 3);
      return sd !== 0 ? sd : b.impressions - a.impressions;
    });
  }, [data, filter]);

  const tabs = [
    { key: 'All',       count: data.length,                                       color: 'var(--fg-strong)' },
    { key: 'Active',    count: data.filter(c => c.status === 'Active').length,    color: 'var(--brand-green-500)' },
    { key: 'Scheduled', count: data.filter(c => c.status === 'Scheduled').length, color: '#f59e0b' },
    { key: 'Ended',     count: data.filter(c => c.status === 'Ended').length,     color: '#60a5fa' },
  ];

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: 'var(--fg-muted)', fontSize: 13 }}>Loading campaigns…</div>;
  if (error) return <div style={{ padding: 40, textAlign: 'center', color: '#dc2626', fontSize: 13 }}>{error}</div>;

  return (
    <div>
      <SummaryStrip campaigns={data} />

      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 18 }}>
        <div style={{ display: 'inline-flex', background: 'var(--bg-surface-3)', border: '1px solid var(--border-soft)', borderRadius: 10, padding: 3 }}>
          {tabs.map(t => {
            const isActive = filter === t.key;
            const tabColor = isActive ? (t.key === 'All' ? '#fff' : t.color ?? '#fff') : (t.color ?? 'var(--fg-strong)');
            return (
              <button key={t.key} onClick={() => setFilter(t.key)} style={{
                border: 'none', background: isActive ? 'var(--bg-elev-strong)' : 'transparent',
                color: tabColor, fontWeight: isActive ? 700 : 500,
                padding: '6px 14px', borderRadius: 7, fontSize: 12, display: 'inline-flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontFamily: 'inherit',
                opacity: isActive ? 1 : 0.65,
              }}>
                {t.key}
                <span style={{ fontSize: 10, fontWeight: 700, background: isActive ? 'rgba(255,255,255,0.12)' : 'var(--bg-surface-3)', color: tabColor, padding: '1px 6px', borderRadius: 999 }}>{t.count}</span>
              </button>
            );
          })}
        </div>
      </div>

      {filtered.length === 0 ? (
        <div className="card" style={{ padding: 60, textAlign: 'center' }}>
          <div style={{ fontSize: 24, marginBottom: 8, opacity: 0.4 }}>◯</div>
          <div style={{ fontWeight: 700, color: 'var(--fg-strong)', marginBottom: 4 }}>No campaigns</div>
        </div>
      ) : (
        <div className="card" style={{ overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: 'var(--bg-surface-2)' }}>
                {['Campaign', 'Status', ...(!isReach ? ['Flight'] : []), 'Plays', 'Impressions', ...(!isReach ? ['Pacing'] : [])].map((h, i) => (
                  <th key={h} style={{ textAlign: i >= (isReach ? 2 : 3) ? 'right' : 'left', padding: '10px 18px', fontSize: 10.5, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--fg-muted)', borderBottom: '1px solid var(--border-subtle)', whiteSpace: 'nowrap' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((c, idx) => (
                <tr key={c.id} className="tbody-row" style={{ borderBottom: idx < filtered.length - 1 ? '1px solid var(--border-subtle)' : 'none', cursor: 'pointer' }} onClick={() => onOpenCampaign(c)}>
                  <td style={{ padding: '14px 18px' }}>
                    <div style={{ fontSize: 13.5, fontWeight: 700, color: 'var(--fg-strong)', marginBottom: 2 }}>{c.name}</div>
                    <div style={{ fontSize: 11.5, color: 'var(--fg-muted)' }}>{c.client.name}</div>
                  </td>
                  <td style={{ padding: '14px 18px' }}><StatusPill status={c.status} /></td>
                  {!isReach && <td style={{ padding: '14px 18px', fontSize: 12, color: 'var(--fg-muted)', whiteSpace: 'nowrap' }} className="num">{c.flight}</td>}
                  <td style={{ padding: '14px 18px', textAlign: 'right' }} className="num">
                    <span style={{ fontWeight: 700, color: 'var(--fg-strong)', fontSize: 13 }}>{fmtFull(c.plays)}</span>
                  </td>
                  <td style={{ padding: '14px 18px', textAlign: 'right' }} className="num">
                    <span style={{ fontWeight: 700, color: 'var(--fg-strong)', fontSize: 13 }}>{fmtFull(c.impressions)}</span>
                  </td>
                  {!isReach && <td style={{ padding: '14px 18px', textAlign: 'right' }}>
                    <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                      <div style={{ width: 60, height: 4, background: 'var(--bg-surface-3)', borderRadius: 2, overflow: 'hidden' }}>
                        <div style={{ width: `${Math.min(100, c.pacing)}%`, height: '100%', background: c.status === 'Ended' ? 'var(--fg-muted)' : c.pacing >= 95 ? 'var(--brand-green-500)' : c.pacing >= 80 ? '#f59e0b' : '#dc2626', borderRadius: 2 }} />
                      </div>
                      <span className="num" style={{ fontSize: 12, fontWeight: 700, color: 'var(--fg-muted)', minWidth: 34, textAlign: 'right' }}>{c.pacing}%</span>
                    </div>
                  </td>}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
